import React, { useState } from 'react';
import {
  Typography,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Paper,
  Divider,
  Alert
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import CustomerLayout from './CustomerLayout'; // ✅ import layout
import { API } from '../../config';
console.log('API base =', API);

// --- add these helpers right here ---
const asNumber = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const money = (v) => asNumber(v).toFixed(2);
const safePremium = (p) => ({
  low: asNumber(p?.low),
  high: asNumber(p?.high),
});
// ------------------------------------//

const formatUSPhoneNumber = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 10);

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const getUSPhoneDigits = (value) =>
  String(value || '').replace(/\D/g, '').slice(0, 10);

const isValidUSPhoneNumber = (value) =>
  getUSPhoneDigits(value).length === 10;

//-------------------------------------//

const decodeJwtPayload = (token) => {
  try {
    if (!token || !token.includes('.')) return null;

    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    );

    return JSON.parse(window.atob(padded));
  } catch (error) {
    console.error('Unable to decode JWT:', error);
    return null;
  }
};

//------------------------------------//

const isCustomerToken = (token) => {
  const payload = decodeJwtPayload(token);
  const role = String(payload?.role || payload?.userType || payload?.type || '').toLowerCase();
  return role === 'customer';
};

const saveCustomerPostToken = (token) => {
  localStorage.setItem('token', token);
  localStorage.setItem('customer_token', token);
  localStorage.setItem('role', 'customer');
  localStorage.setItem('userType', 'customer');
};

const getCustomerPostToken = () => {
  const customerToken = localStorage.getItem('customer_token');
  if (isCustomerToken(customerToken)) return customerToken;

  const token = localStorage.getItem('token');
  if (isCustomerToken(token)) return token;

  return null;
};

const refreshCustomerPostToken = async () => {
  try {
    const response = await fetch(`${API}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.token || !isCustomerToken(data.token)) {
      return null;
    }

    saveCustomerPostToken(data.token);
    return data.token;
  } catch (error) {
    console.error('Customer token refresh failed:', error);
    return null;
  }
};


const CustomerDashboard = () => {
  const history = useHistory();
  const [customerType, setCustomerType] = useState('individual');

  const [formData, setFormData] = useState({
    businessName: '',
    senderFirstName: '',
    senderLastName: '',
    senderAddress: '',
    senderContact: '',
    recipientFirstName: '',
    recipientLastName: '',
    recipientAddress: '',
    recipientContact: '',
    packageInfo: '',
    deliveryType: '',
    specialInstructions: '',
    customerOfferPrice: '',
    // optional coords for AI price + matching
    pickup_lat: '',
    pickup_lng: '',
    dropoff_lat: '',
    dropoff_lng: ''
  });

  // 🔹 Insurance UI (display-only) + premium estimate via /api/ai/price
  const [insuranceOn, setInsuranceOn] = useState(false);
  const [premium, setPremium] = useState(null); // {low, high} or null
  const [insuranceLoading, setInsuranceLoading] = useState(false);
  const [insuranceError, setInsuranceError] = useState('');

   // AI price suggestion state
  const [priceSuggestion, setPriceSuggestion] = useState(null);

  const [priceError, setPriceError] = useState("");
  const [priceLoading, setPriceLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);


  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

    const handlePhoneChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: formatUSPhoneNumber(e.target.value),
    });
  };

  // Simple estimator (you can replace with real values later)
  const recalcPremium = async (distanceMiles = 10, vehicle = 'car', urgency = 'normal') => {
  try {
    setInsuranceLoading(true);
    setInsuranceError('');
    setPremium(null);

    const token = getCustomerPostToken();

    if (!token) {
      setInsuranceOn(false);
      setPremium(null);
      setInsuranceError('Please sign in with a customer account before adding insurance.');
      return;
    }

    const r = await fetch(`${API}/api/ai/price`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ distanceMiles, vehicle, urgency })
    });

    const j = await r.json().catch(() => ({}));

    if (!r.ok) {
      console.error('AI premium error:', j);
      setInsuranceOn(false);
      setPremium(null);
      setInsuranceError(
        j?.message ||
        j?.error ||
        'Insurance premium estimate is temporarily unavailable. Please try again or continue without insurance.'
      );
      return;
    }

    const sp = safePremium(j);

    if (sp.low <= 0 && sp.high <= 0) {
      setInsuranceOn(false);
      setPremium(null);
      setInsuranceError('Insurance premium estimate is temporarily unavailable. Please try again or continue without insurance.');
      return;
    }

    setPremium(j);
  } catch (e) {
    console.error('AI premium fetch failed:', e);
    setInsuranceOn(false);
    setPremium(null);
    setInsuranceError('Insurance premium estimate is temporarily unavailable. Please try again or continue without insurance.');
  } finally {
    setInsuranceLoading(false);
  }
};

const validatePricingInputs = () => {
  const required = [
    { key: 'senderAddress', label: 'Pickup Address' },
    { key: 'recipientAddress', label: 'Drop-off Address' },
    { key: 'packageInfo', label: 'Load Description' },
    { key: 'deliveryType', label: 'Service Type' },
  ];

  const missing = required
    .filter((item) => !String(formData[item.key] || '').trim())
    .map((item) => item.label);

  if (missing.length) {
    return `Please complete these fields before generating a suggested rate: ${missing.join(', ')}.`;
  }

  return '';
};

  // “Get AI Price” button handler
  const handleGetAiPrice = async () => {
  try {
    setPriceError('');
    setPriceSuggestion(null);

    const validationMessage = validatePricingInputs();

    if (validationMessage) {
      setPriceError(validationMessage);
      return;
    }

    setPriceLoading(true);

    const pickup = {
      lat: Number(formData.pickup_lat),
      lng: Number(formData.pickup_lng),
    };

    const dropoff = {
      lat: Number(formData.dropoff_lat),
      lng: Number(formData.dropoff_lng),
    };

    const size = (formData.packageInfo || '').toLowerCase().includes('large')
      ? 'L'
      : 'S';

    const urgency =
      formData.deliveryType === 'express' || formData.deliveryType === 'same_day'
        ? 'urgent'
        : 'normal';

    const resp = await fetch(`${API}/api/pricing/suggest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pickup,
        dropoff,
        size,
        urgency,
        vehicleType: 'car',
        orderId: null,
      }),
    });

    const data = await resp.json().catch(() => ({}));

    if (!resp.ok) {
      console.error('pricing/suggest failed:', data);
      setPriceSuggestion(null);
      setPriceError(
        data?.error ||
          data?.message ||
          'Suggested rate is temporarily unavailable. Please review the load details and try again.'
      );
      return;
    }

    setPriceSuggestion(data);
  } catch (e) {
    console.error('Suggested rate generation failed:', e);
    setPriceSuggestion(null);
    setPriceError(
      'Suggested rate is temporarily unavailable. Please review the load details and try again.'
    );
  } finally {
    setPriceLoading(false);
  }
};

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (submitting) return;        // <-- prevents double submissions
  setSubmitting(true);

const customerOfferPrice = Number(formData.customerOfferPrice);

if (!Number.isFinite(customerOfferPrice) || customerOfferPrice <= 0) {
  alert('Please enter a valid offer price for this load.');
  setSubmitting(false);
  return;
}

const pickupPhoneDigits = getUSPhoneDigits(formData.senderContact);
const dropoffPhoneDigits = getUSPhoneDigits(formData.recipientContact);

if (pickupPhoneDigits.length !== 10) {
  alert('Please enter a valid 10-digit pickup contact number.');
  setSubmitting(false);
  return;
}

if (dropoffPhoneDigits.length !== 10) {
  alert('Please enter a valid 10-digit drop-off contact number.');
  setSubmitting(false);
  return;
}

const payload = {
  customerType,
  ...formData,

  senderContact: pickupPhoneDigits,
  recipientContact: dropoffPhoneDigits,

  // Backend compatibility aliases for pickup phone
  pickupContactNumber: pickupPhoneDigits,
  pickup_contact_number: pickupPhoneDigits,
  pickup_phone: pickupPhoneDigits,
  sender_phone: pickupPhoneDigits,

  // Backend compatibility aliases for drop-off phone
  dropoffContactNumber: dropoffPhoneDigits,
  dropoff_contact_number: dropoffPhoneDigits,
  dropoff_phone: dropoffPhoneDigits,
  recipient_phone: dropoffPhoneDigits,

  customer_price: customerOfferPrice,
  offer_price: customerOfferPrice,
  price: customerOfferPrice,

  // Optional: include what user chose in this order payload
  insuranceOptIn: insuranceOn,
  insuranceEstimateCents: premium ? Math.round(premium.high * 100) : null,
};

  try {
  let token = getCustomerPostToken();

  if (!token) {
    alert('Please sign in with a customer account before posting a load.');
    history.push('/customer/sign-in');
    return;
  }

  const postLoad = (activeToken) => {
    return fetch(`${API}/api/place-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${activeToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
  };

  // DEBUG: confirm where we’re posting & what we send
  console.log('POSTing load to:', `${API}/api/place-order`, 'payload:', payload);

  let response = await postLoad(token);

  // If the 15-minute access token expired while the customer filled the form,
  // refresh once and retry instead of failing with the QA 403/401 behavior.
  if (response.status === 401) {
    const refreshedToken = await refreshCustomerPostToken();

    if (refreshedToken) {
      token = refreshedToken;
      response = await postLoad(token);
    }
  }

   // Surface server errors clearly (now shows status code)
if (!response.ok) {
  const err = await response.json().catch(() => ({}));

  const message =
    err?.error?.message ||
    err?.message ||
    err?.detail?.message ||
    'Unknown error';

  const details =
    err?.error?.details ||
    err?.detail ||
    null;

  alert(
    `Failed to post load (HTTP ${response.status}): ${message}${
      details ? `\n${JSON.stringify(details, null, 2)}` : ''
    }`
  );

  if (response.status === 401) {
    history.push('/customer/sign-in');
  }
  return;
}

const okJson = await response.json().catch(() => null);
console.log('place-order OK:', okJson);

    // Success
    alert('Load posted successfully. You can view it in Load History.');
    setFormData({
      businessName: '',
      senderFirstName: '',
      senderLastName: '',
      senderAddress: '',
      senderContact: '',
      recipientFirstName: '',
      recipientLastName: '',
      recipientAddress: '',
      recipientContact: '',
      packageInfo: '',
      deliveryType: '',
      specialInstructions: '',
      customerOfferPrice: '',
      // reset optional coords too
      pickup_lat: '',
      pickup_lng: '',
      dropoff_lat: '',
      dropoff_lng: ''
    });
    setInsuranceOn(false);
    setPremium(null);
    setPriceSuggestion(null);
  } catch (err) {
    console.error('Error placing order:', err);
    alert(`Something went wrong.\n${err?.message || ''}`);
  } finally {
    setSubmitting(false);        // <-- re-enable submit
  }
};


  const redirectToProfileManagement = () => {
    history.push('/customer/profile-management');
  };

  return (
    <CustomerLayout>
  <Box sx={{ mb: 3 }}>
    <Typography variant="h4" fontWeight={700} gutterBottom>
      Post a Load
    </Typography>

    <Typography variant="body1" color="text.secondary">
      Create a load request for local, regional, or business transportation. 
      Drivers and carriers can view your load, accept it, place bids, 
      or negotiate pricing and transportation terms directly through the platform.
    </Typography>
  </Box>

    <Paper
    elevation={2}
    sx={{
      p: { xs: 2, md: 4 },
      borderRadius: 3,
      width: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden',
    }}
  >

      <form onSubmit={handleSubmit} noValidate>
  <Grid container spacing={2}>
    <Grid item xs={12}>
     <Typography variant="h6" fontWeight={700}>
        Account & Load Information
     </Typography>
      <Divider sx={{ mt: 1, mb: 2 }} />
    </Grid>

    <Grid item xs={12}>
      <TextField
              select
              label="Account Type"
              value={customerType}
              onChange={(e) => setCustomerType(e.target.value)}
              fullWidth
              required
            >
              <MenuItem value="individual">Individual</MenuItem>
              <MenuItem value="business">Business</MenuItem>
            </TextField>
          </Grid>

          {customerType === 'business' && (
            <Grid item xs={12}>
              <TextField
                label="Business Name"
                value={formData.businessName}
                onChange={handleChange('businessName')}
                fullWidth
                required
              />
            </Grid>
          )}

                 {/* Sender Info */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>
              Pickup Details
            </Typography>
            <Divider sx={{ mt: 1, mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Pickup Contact First Name"
              value={formData.senderFirstName}
              onChange={handleChange('senderFirstName')}
              fullWidth
              required
            />
          </Grid>
         <Grid item xs={12} sm={6}>
            <TextField
              label="Pickup Contact Last Name"
              value={formData.senderLastName}
              onChange={handleChange('senderLastName')}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Pickup Address"
              value={formData.senderAddress}
              onChange={handleChange('senderAddress')}
              fullWidth
              required
            />
          </Grid>
                    <Grid item xs={12} sm={6}>
            <TextField
              label="Pickup Contact Number"
              placeholder="(555) 123-4567"
              value={formData.senderContact}
              onChange={handlePhoneChange('senderContact')}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{
                inputMode: 'tel',
                maxLength: 14,
              }}
              error={
                Boolean(formData.senderContact) &&
                !isValidUSPhoneNumber(formData.senderContact)
              }
              helperText={
                Boolean(formData.senderContact) &&
                !isValidUSPhoneNumber(formData.senderContact)
                  ? 'Please enter a valid 10-digit U.S. phone number.'
                  : 'Format: (555) 123-4567'
              }
            />
          </Grid>

                  {/* Load Info */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>
              Load Details
            </Typography>
            <Divider sx={{ mt: 1, mb: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <TextField
             label="Load Description"
              value={formData.packageInfo}
              onChange={handleChange('packageInfo')}
              fullWidth
              required
              multiline
              rows={3}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label="Service Type"
              value={formData.deliveryType}
              onChange={handleChange('deliveryType')}
              fullWidth
              required
            >
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="express">Expedited</MenuItem>
              <MenuItem value="same_day">Same Day</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="dedicated">Dedicated Vehicle</MenuItem>
            </TextField>
          </Grid>


            {/* 🔹 Pricing & Protection */}
           <Grid item xs={12}>
             <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>
                      Pricing & Protection
             </Typography>
             <Divider sx={{ mt: 1, mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
  <TextField
    label="Your Offer Price ($)"
    type="number"
    value={formData.customerOfferPrice}
    onChange={handleChange('customerOfferPrice')}
    fullWidth
    required
    inputProps={{
      min: 1,
      step: '0.01',
    }}
   helperText="Enter your offer price for this load."
  />
</Grid>

        {/* 🔹 Add Insurance + Premium (display-only) */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={insuranceOn}
                  color="primary"
                  onChange={async (e) => {
                    const on = e.target.checked;

                    setInsuranceError('');
                    setPriceError('');

                    if (!on) {
                      setInsuranceOn(false);
                      setPremium(null);
                      return;
                    }

                    const validationMessage = validatePricingInputs();

                    if (validationMessage) {
                      setInsuranceOn(false);
                      setPremium(null);
                      setInsuranceError(
                        'Please complete Pickup Address, Drop-off Address, Load Description, and Service Type before adding insurance.'
                      );
                      return;
                    }

                    setInsuranceOn(true);
                    setPremium(null);
                    await recalcPremium(10, 'car', 'normal');
                  }}
                />
              }
              label="Add Insurance"
            />

            {insuranceError && (
              <Alert severity="warning" sx={{ mt: 1, borderRadius: 2 }}>
                {insuranceError}
              </Alert>
            )}

            {insuranceOn && (
              <Typography
                variant="body2"
                sx={{
                  display: 'block',
                  mt: 1,
                  color: 'text.secondary',
                }}
              >
                {insuranceLoading
                  ? 'Calculating premium…'
                  : premium
                    ? (() => {
                        const sp = safePremium(premium);
                        return `Premium estimate: $${money(sp.low)} – $${money(sp.high)}`;
                      })()
                    : 'Insurance selected. Premium estimate is not available yet.'}
              </Typography>
            )}
          </Grid>

          {/* Special Instructions */}
          <Grid item xs={12}>
            <TextField
              label="Special Instructions"
              value={formData.specialInstructions}
              onChange={handleChange('specialInstructions')}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>

         
                  {/* Recipient Info */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>
              Drop-off Details
            </Typography>
            <Divider sx={{ mt: 1, mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Drop-off Contact First Name"
              value={formData.recipientFirstName}
              onChange={handleChange('recipientFirstName')}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Drop-off Contact Last Name"
              value={formData.recipientLastName}
              onChange={handleChange('recipientLastName')}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Drop-off Address"
              value={formData.recipientAddress}
              onChange={handleChange('recipientAddress')}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Drop-off Contact Number"
              placeholder="(555) 123-4567"
              value={formData.recipientContact}
              onChange={handlePhoneChange('recipientContact')}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{
                inputMode: 'tel',
                maxLength: 14,
              }}
              error={
                Boolean(formData.recipientContact) &&
                !isValidUSPhoneNumber(formData.recipientContact)
              }
              helperText={
                Boolean(formData.recipientContact) &&
                !isValidUSPhoneNumber(formData.recipientContact)
                  ? 'Please enter a valid 10-digit U.S. phone number.'
                  : 'Format: (555) 123-4567'
              }
            />
          </Grid>

          {/* 🔹 AI Price button + badge - hidden until address geocoding is added
<Grid item xs={12}>
  <Box
  sx={{
    display: "flex",
    flexDirection: {
      xs: "column",
      md: "row",
    },
    gap: 2,
    alignItems: {
      xs: "stretch",
      md: "center",
    },
    mt: 1,
  }}
>
  <Button
  type="button"
  variant="outlined"
  color="secondary"
  onClick={handleGetAiPrice}
  disabled={priceLoading}
  sx={{
    minWidth: 240,
    minHeight: 52,
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 2,
    fontSize: "0.95rem",
  }}
>
  {priceLoading ? "Generating..." : "Generate Suggested Rate"}
</Button>

{priceError && (
  <Alert
    severity="warning"
    sx={{
      flex: 1,
      borderRadius: 2,
    }}
  >
    {priceError}
  </Alert>
)}

  {priceSuggestion && (
    <Alert
      severity="info"
      sx={{
        flex: 1,
        alignItems: "flex-start",
        borderRadius: 2,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          lineHeight: 1.7,
        }}
      >
        Suggested Rate:{" "}
        <Box
          component="span"
          sx={{ fontWeight: 800 }}
        >
          {priceSuggestion.target}
        </Box>

        {" "}— suggested negotiation range{" "}
        <Box
          component="span"
          sx={{ fontWeight: 700 }}
        >
          ${priceSuggestion.min} – ${priceSuggestion.max}
        </Box>

        {priceSuggestion.miles != null
          ? ` • Estimated route distance: ${priceSuggestion.miles} mi`
          : ""}
      </Typography>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          mt: 0.75,
          lineHeight: 1.5,
        }}
      >
        AI-assisted pricing estimate based on route distance and load requirements.
      </Typography>
    </Alert>
  )}
</Box>
</Grid>
*/}

          {/* Submit */}
          <Grid item xs={12}>
            <Button
  type="submit"
  variant="contained"
  color="primary"
  size="large"
  fullWidth
  disabled={submitting}
>
  {submitting ? 'Posting Load…' : 'Post Load'}
</Button>

          </Grid>
        </Grid>
      </form>
      </Paper>

      {/*}
      <div style={{ marginTop: '30px' }}>
        <Button
          variant="contained"
          color="secondary"
          size="large"
          onClick={redirectToProfileManagement}
        >
          Profile Management
        </Button>
      </div>
      */}
      
    </CustomerLayout>
  );
};

export default CustomerDashboard;
